import { useEffect, useState } from "react";
import { Save, Download, Upload, Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Field, Area, Select } from "@/components/admin/ui";

export default function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-emerald-800">Loading…</div>;

  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const backup = async () => {
    const { data } = await api.get("/admin/backup");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `healthyroots-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    toast.success("Backup downloaded");
  };
  const restore = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (!window.confirm("Restoring will REPLACE existing content with the backup. Continue?")) return;
    try { const json = JSON.parse(await file.text()); await api.post("/admin/restore", json); toast.success("Restored. Reloading…"); setTimeout(() => window.location.reload(), 1200); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invalid backup file"); }
    e.target.value = "";
  };

  const navItems = s.nav_items || [];
  const setNav = (items) => setS({ ...s, nav_items: items });
  const updNav = (i, patch) => setNav(navItems.map((n, j) => (j === i ? { ...n, ...patch } : n)));
  const addNav = () => setNav([...navItems, { label: "New Link", url: "/", order: navItems.length, visible: true }]);
  const rmNav = (i) => setNav(navItems.filter((_, j) => j !== i));
  const moveNav = (i, d) => {
    const j = i + d; if (j < 0 || j >= navItems.length) return;
    const a = [...navItems]; [a[i], a[j]] = [a[j], a[i]];
    setNav(a.map((n, k) => ({ ...n, order: k })));
  };
  const cta = s.header_cta || { label: "Call to Order", url: `tel:${s.phone_tel || ""}` };
  const setCta = (patch) => setS({ ...s, header_cta: { ...cta, ...patch } });

  return (
    <div data-testid="admin-settings">
      <PageHeader title="Site Settings" subtitle="Business details shown across the website">
        <Btn onClick={save} data-testid="save-settings-btn"><Save size={16} /> Save</Btn>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4">
          <h3 className="font-serif text-lg text-emerald-900">Business</h3>
          <Field label="Business name" value={s.business_name} onChange={(v) => setS({ ...s, business_name: v })} />
          <Field label="Tagline" value={s.tagline} onChange={(v) => setS({ ...s, tagline: v })} />
          <Field label="Phone (display)" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} />
          <Field label="Phone (tel: link, e.g. +12894421265)" value={s.phone_tel} onChange={(v) => setS({ ...s, phone_tel: v })} />
          <Field label="Email" value={s.email} onChange={(v) => setS({ ...s, email: v })} />
          <Field label="Address" value={s.address} onChange={(v) => setS({ ...s, address: v })} />
          <Field label="Appointment note" value={s.appointment_note} onChange={(v) => setS({ ...s, appointment_note: v })} />
          <Area label="Footer text" value={s.footer_text} onChange={(v) => setS({ ...s, footer_text: v })} rows={3} />
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4 h-fit">
          <h3 className="font-serif text-lg text-emerald-900">Backup & Restore</h3>
          <p className="text-sm text-stone-500">Download a full JSON backup of all content, or restore from a previous backup file.</p>
          <div className="flex flex-col gap-3">
            <Btn variant="outline" onClick={backup}><Download size={16} /> Download Backup</Btn>
            <label className="inline-flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-stone-100 text-emerald-900 justify-center">
              <Upload size={16} /> Restore from Backup
              <input type="file" accept="application/json" className="hidden" onChange={restore} />
            </label>
          </div>
          <p className="text-xs text-stone-400">Restore replaces existing content. Always download a fresh backup first.</p>
        </div>
      </div>

      {/* Navigation & Header */}
      <div className="mt-6 bg-white rounded-2xl p-6 border border-stone-200/80" data-testid="nav-manager">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-lg text-emerald-900">Navigation & Header</h3>
            <p className="text-sm text-stone-500">Manage menu links, order, visibility, the call-to-action button, and header style.</p>
          </div>
          <Btn onClick={addNav} data-testid="add-nav-item-btn"><Plus size={16} /> Add Link</Btn>
        </div>

        <div className="space-y-3">
          {navItems.map((n, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-xl border border-stone-200 p-2" data-testid={`nav-item-${i}`}>
              <div className="col-span-4"><Field label={i === 0 ? "Label" : ""} value={n.label} onChange={(v) => updNav(i, { label: v })} /></div>
              <div className="col-span-5"><Field label={i === 0 ? "Destination (e.g. /about-us or https://…)" : ""} value={n.url} onChange={(v) => updNav(i, { url: v })} /></div>
              <div className="col-span-3 flex items-end justify-end gap-1 h-full pb-0.5">
                <button onClick={() => updNav(i, { visible: !(n.visible !== false) })} title="Toggle visibility" data-testid={`nav-visible-${i}`} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500">{n.visible !== false ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <button onClick={() => moveNav(i, -1)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><ChevronUp size={16} /></button>
                <button onClick={() => moveNav(i, 1)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><ChevronDown size={16} /></button>
                <button onClick={() => rmNav(i)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {navItems.length === 0 && <p className="text-sm text-stone-400">No menu links yet — add one, or leave empty to auto-generate from published pages.</p>}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-stone-200">
          <Field label="CTA button label" value={cta.label} onChange={(v) => setCta({ label: v })} />
          <Field label="CTA button link (tel:, /path or https://)" value={cta.url} onChange={(v) => setCta({ url: v })} />
          <Select label="Header appearance" value={s.header_style || "transparent"} onChange={(v) => setS({ ...s, header_style: v })}
            options={[{ value: "transparent", label: "Transparent over hero (white text)" }, { value: "white", label: "Solid white (dark green text)" }]} />
        </div>
        <p className="text-xs text-stone-400 mt-3">Transparent keeps the hero overlay with white menu text and a warm-orange hover. Solid white can be adopted later without redevelopment.</p>
      </div>
    </div>
  );
}
